/* eslint‑disable @typescript-eslint/no‑misused‑promises */
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  useCompleteMultipartMutation,
  useCompletePresignedUploadMutation,
  useCopyBlobsToCompanionsMutation,
  useInitiateUploadMutation,
} from '../../../services/api/blobService';
import type {
  Blob as ApiBlob,
  MultipartUploadResponse,
  PresignedUploadResponse,
} from '../../../services/types/blob-types';
import { UploadProgress as UploadProgressType } from '../../matter-details/documents/types';

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

interface UseUploadManagerUnifiedProps {
  matterId?: string;
  refetch: () => void;
}

interface UploadContext {
  file: File;
  collectionId: string;
  autoSplitLarge: boolean;
  onProgress: (percent: number) => void;
}

interface PresignedContext extends UploadContext {
  initResponse: PresignedUploadResponse;
}

interface MultipartContext extends UploadContext {
  initResponse: MultipartUploadResponse;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Utilities
   ═══════════════════════════════════════════════════════════════════════════ */

/** Compute SHA-1 hash of a file using SubtleCrypto */
export const sha1 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-1', buffer);
  return [...new Uint8Array(hash)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/** Upload file via presigned PUT URL with progress tracking */
const uploadToPresignedUrl = (
  file: File,
  url: string,
  onProgress: (percent: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('PUT', url);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream',
    );
    xhr.send(file);
  });
};

/* ═══════════════════════════════════════════════════════════════════════════
   Hook
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Unified upload manager that uses backend-driven strategy selection.
 * Backend returns presigned URL for files ≤30MB, multipart for larger files.
 */
export const useUploadManagerUnified = ({
  matterId,
  refetch,
}: UseUploadManagerUnifiedProps) => {
  /* ───────────── State ───────────── */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressType[]>(
    [],
  );
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDialogTargetId, setUploadDialogTargetId] = useState<
    string | null
  >(null);

  /* ───────────── RTK Mutations ───────────── */
  const [initiateUpload] = useInitiateUploadMutation();
  const [completePresigned] = useCompletePresignedUploadMutation();
  const [completeMultipart] = useCompleteMultipartMutation();
  const [copyToCompanions] = useCopyBlobsToCompanionsMutation();

  /* ───────────── Progress Helpers ───────────── */

  const bumpProgress = (index: number, patch: Partial<UploadProgressType>) =>
    setUploadProgress((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );

  const initProgressState = (files: File[]) =>
    setUploadProgress(
      files.map((f) => ({
        fileName: f.name,
        progress: 0,
        status: 'pending' as const,
      })),
    );

  /* ───────────── Upload Strategies ───────────── */

  /** Handle presigned URL upload (≤30MB files) */
  const executePresignedUpload = async (
    ctx: PresignedContext,
  ): Promise<ApiBlob> => {
    const { file, collectionId, autoSplitLarge, onProgress, initResponse } =
      ctx;

    await uploadToPresignedUrl(file, initResponse.url, onProgress);

    const hash = await sha1(file);
    return completePresigned({
      matterId: matterId!,
      body: {
        blob_id: initResponse.blob_id,
        key: initResponse.key,
        collection_id: collectionId,
        name: file.name,
        original_filename: file.name,
        sha1: hash,
        auto_split_large: autoSplitLarge,
      },
    }).unwrap();
  };

  /** Handle multipart upload (>30MB files) using Uppy */
  const executeMultipartUpload = async (
    ctx: MultipartContext,
  ): Promise<ApiBlob> => {
    const { file, collectionId, autoSplitLarge, onProgress, initResponse } =
      ctx;
    const { blob_id, key, upload_id, part_urls } = initResponse;

    // Build a lookup map for part URLs (part_number -> url)
    const partUrlMap = new Map(part_urls.map((p) => [p.part_number, p.url]));

    let created: ApiBlob | undefined;

    const uppy = new Uppy({ debug: false, allowMultipleUploads: false }).use(
      AwsS3,
      {
        shouldUseMultipart: () => true,
        createMultipartUpload: async () => ({ key, uploadId: upload_id }),

        // Use pre-generated URLs from initiate response (no extra API calls!)
        signPart: async (_f, opts) => {
          const url = partUrlMap.get(opts.partNumber);
          if (!url) {
            throw new Error(`No presigned URL for part ${opts.partNumber}`);
          }
          return { url };
        },

        completeMultipartUpload: async (_f, o) => {
          const hash = await sha1(file);
          created = await completeMultipart({
            matterId: matterId!,
            body: {
              blob_id,
              key: o.key,
              upload_id: o.uploadId,
              parts: o.parts.map((p) => ({
                e_tag: p.ETag || '',
                part_number: p.PartNumber || 0,
              })),
              collection_id: collectionId,
              name: file.name,
              original_filename: file.name,
              sha1: hash,
              auto_split_large: autoSplitLarge,
            },
          }).unwrap();
          return { location: created.download_url };
        },

        listParts: async () => [],
        abortMultipartUpload: async () => {},
        getUploadParameters: async () => ({
          method: 'POST',
          url: '',
          fields: {},
        }),
      },
    );

    uppy.addFile({ name: file.name, type: file.type, data: file });
    uppy.on('upload-progress', (_f, progress) => {
      onProgress((progress.bytesUploaded / (progress.bytesTotal ?? 1)) * 100);
    });

    await uppy.upload();
    return created!;
  };

  /** Upload a single file using the appropriate strategy */
  const uploadSingleFile = async (ctx: UploadContext): Promise<ApiBlob> => {
    const { file, collectionId } = ctx;

    const initResponse = await initiateUpload({
      matterId: matterId!,
      body: {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        collection_id: collectionId,
        file_size: file.size,
      },
    }).unwrap();

    if (initResponse.upload_type === 'presigned') {
      return executePresignedUpload({ ...ctx, initResponse });
    }
    return executeMultipartUpload({ ...ctx, initResponse });
  };

  /* ───────────── Public Upload Methods ───────────── */

  /** Upload files to a collection with progress tracking.
   *  Returns the IDs of successfully created blobs (empty array on error). */
  const handleUploadToCollection = async (
    files: File[],
    collectionId: string,
    autoSplitLarge = false,
    uploadToCompanions = false,
    targetCompanionMatterIds?: string[],
  ): Promise<string[]> => {
    setUploadError(null);
    if (!matterId) return [];

    initProgressState(files);

    try {
      const uploadedBlobIds: string[] = [];

      await Promise.all(
        files.map(async (file, index) => {
          bumpProgress(index, { status: 'uploading', progress: 0 });

          const created = await uploadSingleFile({
            file,
            collectionId,
            autoSplitLarge,
            onProgress: (progress) => bumpProgress(index, { progress }),
          });

          uploadedBlobIds.push(created.id);
          bumpProgress(index, { status: 'completed', progress: 100 });
        }),
      );

      if (uploadToCompanions && uploadedBlobIds.length > 0) {
        await copyToCompanions({
          matterId,
          body: {
            blob_ids: uploadedBlobIds,
            dry_run: false,
            target_matter_ids: targetCompanionMatterIds,
          },
        })
          .then(() => toast.success('Files transferred to companion matters'))
          .catch((err) => {
            console.error(err);
            toast.error('Failed to transfer files to companion matters');
          });
      }

      refetch();
      return uploadedBlobIds;
    } catch (err) {
      console.error(err);
      setUploadError('Failed to upload one or more files. Please try again.');
      return [];
    }
  };

  /** Upload files and return created blobs (no progress UI) */
  const handleUploadToCollectionAndReturn = async (
    files: File[],
    collectionId: string,
    autoSplitLarge = false,
  ): Promise<ApiBlob[]> => {
    if (!matterId) return [];

    const results = await Promise.all(
      files.map((file) =>
        uploadSingleFile({
          file,
          collectionId,
          autoSplitLarge,
          onProgress: () => {},
        }),
      ),
    );

    return results;
  };

  /* ───────────── UI Helpers ───────────── */

  const showUploadDialog = (id: string) => {
    setUploadDialogTargetId(id);
    setUploadDialogOpen(true);
  };

  const closeUploadDialog = () => setUploadDialogOpen(false);

  const triggerUpload = (targetCollectionId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e) => {
      const picked = Array.from((e.target as HTMLInputElement).files || []);
      if (picked.length && targetCollectionId) {
        handleUploadToCollection(picked, targetCollectionId, false, false);
      }
    };
    input.click();
  };

  const handleDrop = async (
    e: React.DragEvent,
    targetCollectionId?: string,
  ) => {
    const picked = Array.from(e.dataTransfer.files);
    if (picked.length && targetCollectionId) {
      await handleUploadToCollection(picked, targetCollectionId, false, false);
    }
    return picked.length > 0;
  };

  /* ───────────── Return ───────────── */

  return {
    // State
    uploadProgress,
    uploadError,
    uploadDialogOpen,
    uploadDialogTargetId,

    // Actions
    triggerUpload,
    handleDrop,
    handleUploadToCollection,
    handleUploadToCollectionAndReturn,

    // State management
    dismissError: () => setUploadError(null),
    clearUploads: () => setUploadProgress([]),
    showUploadDialog,
    closeUploadDialog,
  };
};

export default useUploadManagerUnified;
